import { useTheme } from '@/components/ThemeProvider';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertCircle, ArrowDownRight, ArrowUpRight, CircleDollarSign, Clock, TrendingUp } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
    CartesianGrid, Cell, Legend, Line, LineChart,
    Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis
} from 'recharts';

// Mock data for financial analytics
const mockRevenueStats = {
   total: 150000,
   paid: 95000,
   pending: 40000,
   overdue: 15000,
   monthlyGrowth: 12
};

// Mock revenue trend data for recharts format
const mockRevenueTrendData = [
   { month: 'Jan', revenue: 20000 },
   { month: 'Feb', revenue: 35000 },
   { month: 'Mar', revenue: 42000 },
   { month: 'Apr', revenue: 58000 },
   { month: 'May', revenue: 72000 },
   { month: 'Jun', revenue: 91000 },
   { month: 'Jul', revenue: 150000 }
];

const FinancialAnalytics = ({ data }) => {
   const [useMockData, setUseMockData] = useState(false);
   const [revenueStats, setRevenueStats] = useState({});
   const [revenueTrendData, setRevenueTrendData] = useState([]);

   const { theme } = useTheme();
   const isDark = theme === 'dark';

   useEffect(() => {
      // Check if mock data is enabled in localStorage
      const mockEnabled = localStorage.getItem('useMockFinancialData') === 'true';
      setUseMockData(mockEnabled);

      if (mockEnabled) {
         setRevenueStats(mockRevenueStats);
         setRevenueTrendData(mockRevenueTrendData);
      } else {
         setRevenueStats(data.revenueStats);

         // Use real data or fallback to empty data structure
         const realTrendData = [
            { month: 'Jan', revenue: 4000 },
            { month: 'Feb', revenue: 3000 },
            { month: 'Mar', revenue: 5000 },
            { month: 'Apr', revenue: 7000 },
            { month: 'May', revenue: 6000 },
            { month: 'Jun', revenue: 8000 },
            { month: 'Jul', revenue: data.revenueStats.total }
         ];

         setRevenueTrendData(realTrendData);
      }
   }, [data, isDark]);

   // Prepare data for pie chart
   const pieData = [
      {
         name: 'Paid',
         value: revenueStats.paid || 0,
         color: '#22c55e'
      },
      {
         name: 'Pending',
         value: revenueStats.pending || 0,
         color: '#f59e0b'
      },
      {
         name: 'Overdue',
         value: revenueStats.overdue || 0,
         color: '#ef4444'
      }
   ];

   return (
      <div className="space-y-6">
         {useMockData && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3 mb-4">
               <p className="text-sm text-yellow-800 dark:text-yellow-300">
                  Using mock financial data. Toggle this off in Dev Settings to use real data.
               </p>
            </div>
         )}

         {/* Revenue Stats Cards */}
         <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <Card className="overflow-hidden shadow-md transition-all hover:shadow-lg">
               <CardHeader className="pb-2 bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20">
                  <div className="flex justify-between items-center">
                     <CardTitle className="text-sm font-medium">
                        Total Revenue
                        {revenueStats.monthlyGrowth > 0 && (
                           <span className="text-xs text-green-600 ml-2 flex items-center">
                              <ArrowUpRight className="h-3 w-3 mr-1" />
                              +{revenueStats.monthlyGrowth}%
                           </span>
                        )}
                        {revenueStats.monthlyGrowth < 0 && (
                           <span className="text-xs text-red-600 ml-2 flex items-center">
                              <ArrowDownRight className="h-3 w-3 mr-1" />
                              {revenueStats.monthlyGrowth}%
                           </span>
                        )}
                     </CardTitle>
                     <TrendingUp className="h-5 w-5 text-green-600" />
                  </div>
                  <CardDescription>Total earnings collected</CardDescription>
               </CardHeader>
               <CardContent className="pt-4">
                  <div className="text-3xl font-bold text-green-600">
                     ₱{revenueStats.total?.toLocaleString()}
                  </div>
               </CardContent>
            </Card>

            <Card className="overflow-hidden shadow-md transition-all hover:shadow-lg">
               <CardHeader className="pb-2 bg-gradient-to-r from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20">
                  <div className="flex justify-between items-center">
                     <CardTitle className="text-sm font-medium">Paid Revenue</CardTitle>
                     <CircleDollarSign className="h-5 w-5 text-emerald-600" />
                  </div>
                  <CardDescription>Successfully collected</CardDescription>
               </CardHeader>
               <CardContent className="pt-4">
                  <div className="text-3xl font-bold text-emerald-600">
                     ₱{revenueStats.paid?.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground mt-2">
                     {Math.round((revenueStats.paid / revenueStats.total) * 100)}% of total revenue
                  </div>
               </CardContent>
            </Card>

            <Card className="overflow-hidden shadow-md transition-all hover:shadow-lg">
               <CardHeader className="pb-2 bg-gradient-to-r from-amber-50 to-amber-100 dark:from-amber-900/20 dark:to-amber-800/20">
                  <div className="flex justify-between items-center">
                     <CardTitle className="text-sm font-medium">Pending Payments</CardTitle>
                     <Clock className="h-5 w-5 text-amber-600" />
                  </div>
                  <CardDescription>Awaiting payment</CardDescription>
               </CardHeader>
               <CardContent className="pt-4">
                  <div className="text-3xl font-bold text-amber-500">
                     ₱{revenueStats.pending?.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground mt-2">
                     {Math.round((revenueStats.pending / revenueStats.total) * 100)}% of total revenue
                  </div>
               </CardContent>
            </Card>

            <Card className="overflow-hidden shadow-md transition-all hover:shadow-lg">
               <CardHeader className="pb-2 bg-gradient-to-r from-red-50 to-red-100 dark:from-red-900/20 dark:to-red-800/20">
                  <div className="flex justify-between items-center">
                     <CardTitle className="text-sm font-medium">Overdue Payments</CardTitle>
                     <AlertCircle className="h-5 w-5 text-red-600" />
                  </div>
                  <CardDescription>Past due date</CardDescription>
               </CardHeader>
               <CardContent className="pt-4">
                  <div className="text-3xl font-bold text-red-500">
                     ₱{revenueStats.overdue?.toLocaleString()}
                  </div>
                  <div className="text-xs text-muted-foreground mt-2">
                     {Math.round((revenueStats.overdue / revenueStats.total) * 100)}% of total revenue
                  </div>
               </CardContent>
            </Card>
         </div>

         {/* Charts */}
         <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Revenue Trend Chart */}
            <Card className="shadow-md">
               <CardHeader>
                  <CardTitle>Revenue Trend</CardTitle>
                  <CardDescription>Monthly revenue collection over time</CardDescription>
               </CardHeader>
               <CardContent>
                  <div className="h-[300px]">
                     <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                           data={revenueTrendData}
                           margin={{ top: 20, right: 30, bottom: 20, left: 25 }}
                        >
                           <CartesianGrid strokeDasharray="3 3" stroke={isDark ? "#1f2937" : "#e5e7eb"} />
                           <XAxis
                              dataKey="month"
                              tick={{ fill: isDark ? "#e5e7eb" : "#374151" }}
                           />
                           <YAxis
                              tick={{ fill: isDark ? "#e5e7eb" : "#374151" }}
                              tickFormatter={(value) => value >= 1000 ? `${value/1000}k` : value}
                              width={45}
                           />
                           <Tooltip
                              formatter={(value) => `₱${value.toLocaleString()}`}
                              contentStyle={{
                                 backgroundColor: isDark ? "#1f2937" : "#ffffff",
                                 color: isDark ? "#ffffff" : "#000000",
                                 border: `1px solid ${isDark ? "#374151" : "#e5e7eb"}`,
                              }}
                           />
                           <Legend />
                           <Line
                              type="monotone"
                              dataKey="revenue"
                              name="Revenue"
                              stroke="#10b981"
                              activeDot={{ r: 8 }}
                              strokeWidth={2}
                           />
                        </LineChart>
                     </ResponsiveContainer>
                  </div>
               </CardContent>
            </Card>

            {/* Revenue Distribution Pie Chart */}
            <Card className="shadow-md">
               <CardHeader>
                  <CardTitle>Revenue Distribution</CardTitle>
                  <CardDescription>Breakdown of payment status</CardDescription>
               </CardHeader>
               <CardContent>
                  <div className="h-[300px]">
                     <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                           <Pie
                              data={pieData}
                              cx="50%"
                              cy="50%"
                              labelLine={false}
                              outerRadius={80}
                              innerRadius={50}
                              fill="#8884d8"
                              dataKey="value"
                              nameKey="name"
                              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                           >
                              {pieData.map((entry, index) => (
                                 <Cell key={`cell-${index}`} fill={entry.color} />
                              ))}
                           </Pie>
                           <Tooltip
                              formatter={(value) => `₱${value.toLocaleString()}`}
                              contentStyle={{
                                 backgroundColor: isDark ? "#1f2937" : "#ffffff",
                                 color: isDark ? "#ffffff" : "#000000",
                                 border: `1px solid ${isDark ? "#374151" : "#e5e7eb"}`,
                              }}
                           />
                           <Legend
                              verticalAlign="bottom"
                              height={36}
                           />
                        </PieChart>
                     </ResponsiveContainer>
                  </div>
               </CardContent>
            </Card>
         </div>
      </div>
   );
};

export default FinancialAnalytics;
